// Import the query hooks directly from the dashboardApi module: the shared
// `app/services` barrel re-exports both authApi and dashboardApi, and several
// hook names (e.g. useGetCurrentUserQuery) collide between them. Importing from
// the module avoids the ambiguous wildcard re-export.
import { dashboardApi } from 'app/services/dashboardApi';
import { getKsdmConfig } from 'helpers/index';

const {
  useGetCurrentUserQuery,
  useGetPermissionsQuery,
  useGetColumnsQuery,
  useGetWorkflowTasksQuery,
  useGetCriticalTaskQuery,
  useGetCompaniesQuery,
  useGetContactsQuery,
  useGetPartnersQuery,
  useGetMfgUsersQuery,
  useGetMarketTypesQuery,
} = dashboardApi;

/** Resolve the bootstrap user id from the server-injected config (body[data-user_id] equivalent). */
export const getBootUserId = (): number | null => {
  const raw = getKsdmConfig()?.user as { id?: unknown } | null | undefined;
  const id = raw?.id;
  if (id == null) return null;
  const n = Number(id);
  return Number.isNaN(n) ? null : n;
};

/**
 * Current dashboard user — fetched from /api/v1.0/users/{id} where id comes from
 * window.__KSDM_CONFIG__.user.id. Skips when no id is available.
 */
export function useCurrentUser() {
  const id = getBootUserId();
  return useGetCurrentUserQuery(id as number, { skip: id == null });
}

/** Permission map (gates the board). */
export function usePermissions() {
  return useGetPermissionsQuery();
}

/** Role-filtered column defs. */
export function useColumns() {
  return useGetColumnsQuery();
}

/** Status-rail workflow tasks. */
export function useWorkflowTasks() {
  return useGetWorkflowTasksQuery();
}

/** Critical task (single {task_id}). */
export function useCriticalTask() {
  return useGetCriticalTaskQuery();
}

// ---- FK lookup collections (fetched once, cached/deduped by RTK Query) ----

/** company id → name (`/api/v1.0/projects/companies/`). rel_model='company'. */
export function useCompanies() {
  return useGetCompaniesQuery();
}
/** contact id → name (`/api/v1.0/projects/contacts/`). rel_model='company_contact'. */
export function useContacts() {
  return useGetContactsQuery();
}
/** partner company id → name (`/cms/1/v1.0.0/mfg/partners/`). VALUE coll for rep_company. */
export function usePartners() {
  return useGetPartnersQuery();
}
/** mfg user id → first_name (`/api/v1.0/mfg/1/users/`). rep_user value; rep_company guard. */
export function useMfgUsers() {
  return useGetMfgUsersQuery();
}
/** market type id → name (`/cms/1/v1.0.0/mfg/market_types/`). rel_model='market_type'. */
export function useMarketTypes() {
  return useGetMarketTypesQuery();
}
